import { CircularProgress, Dialog } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import useDialogActions from "../useDialogActions";
import { Button } from "antd";
import {
  Currency,
  SettlementStatus,
  callNonStoreApi,
  formatPrice,
  handleCopyToClipboard,
  handleDownloadCSV,
} from "../utils/utils";
import {
  GetSettlementDetail,
  SendSettlementCommunication,
  Settle,
  VerifyBeneficiary,
} from "../utils/ApiEndpoints";
import { formatList } from "../Components/clientsHelpers";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../utils/firebase";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { enqueueSnackbar } from "notistack";
import { format } from "date-fns";
import { ContentCopy, Check, Close, Warning, CheckCircle, Info } from "@mui/icons-material";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Design tokens
const colors = {
  bg: "#fafafa",
  surface: "#ffffff",
  border: "#e5e7eb",
  borderLight: "#f3f4f6",
  text: "#111827",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
  accent: "#6366f1",
  accentLight: "#eef2ff",
  accentDark: "#4f46e5",
  success: "#059669",
  successLight: "#d1fae5",
  danger: "#dc2626",
};

// Styled Components
const TriggerButton = styled(Button)`
  border-radius: 6px;
  font-weight: 500;
  font-size: 12px;
  height: 28px;
  padding: 0 12px;
  border-color: ${colors.border};

  &:hover {
    border-color: ${colors.accent} !important;
    color: ${colors.accent} !important;
  }
`;

const DialogContainer = styled.div`
  animation: ${fadeIn} 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const DialogHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.border};
  background: ${colors.surface};
`;

const DialogTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.textMuted};
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: ${colors.borderLight};
    color: ${colors.text};
  }
`;

const DialogBody = styled.div`
  padding: 20px;
  animation: ${slideUp} 0.25s ease;
  background: ${colors.bg};
  overflow-y: auto;
  max-height: calc(90vh - 57px);
`;

const Section = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const SectionHeader = styled.div`
  padding: 10px 14px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.borderLight};
`;

const SectionTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoList = styled.div``;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid ${colors.borderLight};
  background: ${colors.surface};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: ${colors.textSecondary};
`;

const InfoValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text};
`;

const MonoText = styled.span`
  font-family: "SF Mono", Monaco, Consolas, monospace;
  font-size: 12px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${colors.borderLight};
  border: none;
  border-radius: 4px;
  color: ${colors.textMuted};
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: ${colors.accentLight};
    color: ${colors.accent};
  }
`;

const PayoutCard = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.accent} 0%,
    ${colors.accentDark} 100%
  );
  border-radius: 10px;
  padding: 24px 20px;
  margin-bottom: 16px;
  color: white;
  text-align: center;
`;

const PayoutLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.85;
  margin-bottom: 8px;
`;

const PayoutAmount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const PayoutCopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const PayoutNote = styled.div`
  font-size: 13px;
  opacity: 0.75;
  margin-top: 8px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  background: ${colors.text};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: #000;
  }

  &:disabled {
    background: ${colors.textMuted};
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  animation: ${fadeIn} 0.2s ease;
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: ${colors.textSecondary};
`;

const VerifyBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 500;
  background: ${(p) =>
    p.$type === "success"
      ? "#d1fae5"
      : p.$type === "warning"
        ? "#fef3c7"
        : p.$type === "info"
          ? "#dbeafe"
          : "#fee2e2"};
  color: ${(p) =>
    p.$type === "success"
      ? "#065f46"
      : p.$type === "warning"
        ? "#92400e"
        : p.$type === "info"
          ? "#1e40af"
          : "#991b1b"};
  border: 1px solid
    ${(p) =>
      p.$type === "success"
        ? "#a7f3d0"
        : p.$type === "warning"
          ? "#fde68a"
          : p.$type === "info"
            ? "#bfdbfe"
            : "#fecaca"};
`;

const BankDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.borderLight};
  background: ${colors.surface};

  &:last-child {
    border-bottom: none;
  }
`;

const BankLabel = styled.span`
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const BankValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text};
  font-family: "SF Mono", Monaco, Consolas, monospace;
`;

const MismatchValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.danger};
  font-family: "SF Mono", Monaco, Consolas, monospace;
  text-decoration: line-through;
`;

function SettlementDialog({ status, settlementId, updateSettlementDoc }) {
  const { closeDialog, isDialogOpen, openDialog } = useDialogActions();
  const [settlementDocumentData, setSettlementDocumentData] = useState();
  const [loading, setLoading] = useState(true);
  const [isSendingCommunications, setIsSendingCommunication] = useState(false);
  const [isSettlingOrder, setIsSettlingOrder] = useState(false);
  const [verifyData, setVerifyData] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(true);

  const handleGetSettlementId = useCallback(async () => {
    setLoading(true);
    setVerifyLoading(true);
    try {
      const [settlementRes, verifyRes] = await Promise.all([
        callNonStoreApi(
          GetSettlementDetail.replace(":settlementId", settlementId)
        ),
        callNonStoreApi(
          `${VerifyBeneficiary}?settlementId=${settlementId}`
        ),
      ]);
      setSettlementDocumentData(settlementRes.data.docs);
      setVerifyData(verifyRes.data);
    } catch (error) {
      console.error("Error loading settlement details:", error);
      enqueueSnackbar("Failed to load settlement details", { variant: "error" });
    } finally {
      setLoading(false);
      setVerifyLoading(false);
    }
  }, [settlementId]);

  const getButtonText = (status) => {
    if (
      status === SettlementStatus.Pending ||
      status === SettlementStatus.ReadyToProcess ||
      status === "ready_to_process" ||
      status === SettlementStatus.Processing
    ) {
      return "Settle";
    }
    if (status === SettlementStatus.Failed || status === "Failed") {
      return "Retry";
    }
    return "Resend";
  };

  const handleGenerateSettlementPDF = async (updatedSettlementDoc, list) => {
    const csvData = await handleDownloadCSV(list);
    const csvRows = csvData.split("\n").map((row) => row.split(","));
    const headers = csvRows[0];
    const dataRows = csvRows.slice(1);
    const doc = new jsPDF();
    doc.autoTable({
      head: [headers],
      body: dataRows,
    });
    const csvBlob = new Blob([csvData], { type: "text/csv" });
    const docName =
      updatedSettlementDoc.brandPage?.name ?? updatedSettlementDoc.username;
    const storageRef = ref(
      storage,
      `settlementData/${docName}_${updatedSettlementDoc.settlementDate}_${updatedSettlementDoc.settlementId}.csv`
    );

    try {
      const uploadResult = await uploadBytes(storageRef, csvBlob);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading PDF:", error);
    }
  };

  const handleSendClientCommunication = async (
    settlementDoc,
    settlementsUrl
  ) => {
    try {
      const res = await callNonStoreApi(SendSettlementCommunication, {
        settlementDoc,
        settlementsUrl,
      });
      return res.data.settlementDoc;
    } catch (error) {
      console.log(error);
    }
  };

  const handleMakeManualSettlement = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      let settlementsUrl;
      setIsSettlingOrder(true);
      const res = await callNonStoreApi(Settle, { settlementId });
      enqueueSnackbar("Amount marked as settled", { variant: "success" });
      try {
        settlementsUrl = await handleGenerateSettlementPDF(
          res.data.updatedSettlementDoc,
          formatList(
            res.data.updatedSaleDocs,
            res.data.updatedSettlementDoc.orders.tdsPercent
          )
        );
        enqueueSnackbar("Settlement details generated", { variant: "success" });
      } catch (error) {
        console.log("Error generating PDF");
      }
      const updatedSettlementDoc = await handleSendClientCommunication(
        res.data.updatedSettlementDoc,
        settlementsUrl
      );
      enqueueSnackbar("Communication sent to client", { variant: "success" });
      updateSettlementDoc(updatedSettlementDoc);
      closeDialog();
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Failed to process settlement", { variant: "error" });
    } finally {
      setIsSettlingOrder(false);
    }
  };

  const sendCommunication = async (settlementId) => {
    try {
      setIsSendingCommunication(true);
      await callNonStoreApi(SendSettlementCommunication, { settlementId });
      enqueueSnackbar("Communication sent", { variant: "success" });
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Failed to send communication", { variant: "error" });
    } finally {
      setIsSendingCommunication(false);
    }
  };

  useEffect(() => {
    if (isDialogOpen) handleGetSettlementId();
  }, [isDialogOpen, handleGetSettlementId]);

  const handleCopy = async (text) => {
    await handleCopyToClipboard(text);
    enqueueSnackbar("Copied to clipboard", { variant: "success" });
  };

  const settlementName = settlementDocumentData
    ? `${format(new Date(), "MMM_dd")}_${settlementDocumentData.settlementId}_${
        settlementDocumentData.brandPage?.name ??
        settlementDocumentData.userName ??
        settlementDocumentData.username ??
        ""
      }`
    : "";

  return (
    <>
      <TriggerButton
        onClick={(e) => {
          e.stopPropagation();
          status === SettlementStatus.Success || status === "Settled"
            ? sendCommunication(settlementId)
            : openDialog();
        }}
      >
        {isSendingCommunications ? (
          <CircularProgress size={12} sx={{ color: colors.textMuted }} />
        ) : (
          getButtonText(status)
        )}
      </TriggerButton>

      <Dialog
        open={isDialogOpen}
        onClose={() => {
          if (loading) return;
          setVerifyData(null);
          setVerifyLoading(true);
          closeDialog();
        }}
        PaperProps={{
          sx: {
            width: "440px",
            maxWidth: "90vw",
            borderRadius: "12px",
            maxHeight: "90vh",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <LoadingState>
            <CircularProgress size={28} sx={{ color: colors.accent }} />
            <LoadingText>Loading settlement details...</LoadingText>
          </LoadingState>
        ) : (
          <DialogContainer>
            <DialogHeaderStyled>
              <DialogTitle>Process Settlement</DialogTitle>
              <CloseButton onClick={closeDialog} type="button">
                <Close sx={{ fontSize: 20 }} />
              </CloseButton>
            </DialogHeaderStyled>

            <form onSubmit={handleMakeManualSettlement}>
              <DialogBody>
                <PayoutCard>
                  <PayoutLabel>Amount to Pay</PayoutLabel>
                  <PayoutAmount>
                    {formatPrice(
                      settlementDocumentData.orders.totalAmountAfterTds ??
                        settlementDocumentData.orders.totalPayoutAfterTds,
                      Currency.INR
                    )}
                    <PayoutCopyBtn
                      type="button"
                      onClick={() =>
                        handleCopy(
                          (
                            (settlementDocumentData.orders
                              .totalAmountAfterTds ??
                              settlementDocumentData.orders
                                .totalPayoutAfterTds) / 100
                          ).toString()
                        )
                      }
                    >
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </PayoutCopyBtn>
                  </PayoutAmount>
                  <PayoutNote>After TDS deduction</PayoutNote>
                </PayoutCard>

                {verifyLoading ? (
                  <Section>
                    <div style={{ padding: "20px", textAlign: "center" }}>
                      <CircularProgress size={20} sx={{ color: colors.accent }} />
                      <div style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary }}>
                        Verifying beneficiary...
                      </div>
                    </div>
                  </Section>
                ) : verifyData ? (
                  <>
                    {verifyData.matched ? (
                      <VerifyBanner $type="success">
                        <CheckCircle sx={{ fontSize: 18 }} />
                        {verifyData.message || "Bank details match. Safe to proceed."}
                      </VerifyBanner>
                    ) : !verifyData.beneficiaryExists ? (
                      <VerifyBanner $type="info">
                        <Info sx={{ fontSize: 18 }} />
                        No beneficiary found on Cashfree. A new one will be created during settlement.
                      </VerifyBanner>
                    ) : (
                      <VerifyBanner $type="error">
                        <Warning sx={{ fontSize: 18 }} />
                        {verifyData.message || "Bank detail mismatch! Settlement blocked."}
                      </VerifyBanner>
                    )}

                    <Section>
                      <SectionHeader>
                        <SectionTitle>
                          Brand Page Bank Details
                          {verifyData.brandPageName && ` — ${verifyData.brandPageName}`}
                        </SectionTitle>
                      </SectionHeader>
                      <InfoList>
                        <BankDetailRow>
                          <BankLabel>Account Holder</BankLabel>
                          <BankValue>{verifyData.brandPageBankDetails?.accountHolderName || "—"}</BankValue>
                        </BankDetailRow>
                        <BankDetailRow>
                          <BankLabel>Account Number</BankLabel>
                          <BankValue>{verifyData.brandPageBankDetails?.accountNumber || "—"}</BankValue>
                        </BankDetailRow>
                        <BankDetailRow>
                          <BankLabel>IFSC</BankLabel>
                          <BankValue>{verifyData.brandPageBankDetails?.ifsc || "—"}</BankValue>
                        </BankDetailRow>
                      </InfoList>
                    </Section>

                    {verifyData.beneficiaryExists && verifyData.cashfreeBankDetails && (
                      <Section>
                        <SectionHeader>
                          <SectionTitle>Cashfree Beneficiary Details</SectionTitle>
                        </SectionHeader>
                        <InfoList>
                          <BankDetailRow>
                            <BankLabel>Account Holder</BankLabel>
                            {verifyData.matched ? (
                              <BankValue>{verifyData.cashfreeBankDetails.accountHolderName}</BankValue>
                            ) : (
                              <MismatchValue>{verifyData.cashfreeBankDetails.accountHolderName}</MismatchValue>
                            )}
                          </BankDetailRow>
                          <BankDetailRow>
                            <BankLabel>Account Number</BankLabel>
                            {verifyData.matched ? (
                              <BankValue>{verifyData.cashfreeBankDetails.accountNumber}</BankValue>
                            ) : (
                              <MismatchValue>{verifyData.cashfreeBankDetails.accountNumber}</MismatchValue>
                            )}
                          </BankDetailRow>
                          <BankDetailRow>
                            <BankLabel>IFSC</BankLabel>
                            {verifyData.matched ? (
                              <BankValue>{verifyData.cashfreeBankDetails.ifsc}</BankValue>
                            ) : (
                              <MismatchValue>{verifyData.cashfreeBankDetails.ifsc}</MismatchValue>
                            )}
                          </BankDetailRow>
                        </InfoList>
                      </Section>
                    )}
                  </>
                ) : null}

                <Section>
                  <SectionHeader>
                    <SectionTitle>Settlement Details</SectionTitle>
                  </SectionHeader>
                  <InfoList>
                    <InfoRow>
                      <InfoLabel>Name</InfoLabel>
                      <InfoValue>
                        <MonoText title={settlementName}>
                          {settlementName}
                        </MonoText>
                        <CopyBtn
                          type="button"
                          onClick={() => handleCopy(settlementName)}
                        >
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </CopyBtn>
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>ID</InfoLabel>
                      <InfoValue>
                        <MonoText>
                          {settlementDocumentData.settlementId}
                        </MonoText>
                        <CopyBtn
                          type="button"
                          onClick={() =>
                            handleCopy(settlementDocumentData.settlementId)
                          }
                        >
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </CopyBtn>
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Orders</InfoLabel>
                      <InfoValue>
                        {settlementDocumentData.orders?.list?.length ??
                          settlementDocumentData.orders?.count ??
                          0}{" "}
                        orders
                      </InfoValue>
                    </InfoRow>
                  </InfoList>
                </Section>

                <Section>
                  <SectionHeader>
                    <SectionTitle>Breakdown</SectionTitle>
                  </SectionHeader>
                  <InfoList>
                    <InfoRow>
                      <InfoLabel>Total Amount</InfoLabel>
                      <InfoValue>
                        {formatPrice(
                          settlementDocumentData.orders.totalPayout,
                          Currency.INR
                        )}
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>
                        TDS (
                        {(settlementDocumentData.orders.tdsPercent ?? 0) * 100}
                        %)
                      </InfoLabel>
                      <InfoValue style={{ color: colors.danger }}>
                        −{" "}
                        {formatPrice(
                          settlementDocumentData.orders.tds,
                          Currency.INR
                        )}
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Net Payable</InfoLabel>
                      <InfoValue
                        style={{ fontWeight: 600, color: colors.success }}
                      >
                        {formatPrice(
                          settlementDocumentData.orders.totalAmountAfterTds ??
                            settlementDocumentData.orders.totalPayoutAfterTds,
                          Currency.INR
                        )}
                      </InfoValue>
                    </InfoRow>
                  </InfoList>
                </Section>

                <SubmitBtn
                  type="submit"
                  disabled={
                    isSettlingOrder ||
                    verifyLoading ||
                    (verifyData && verifyData.beneficiaryExists && !verifyData.matched)
                  }
                >
                  {isSettlingOrder ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      <span>Processing...</span>
                    </>
                  ) : verifyData && verifyData.beneficiaryExists && !verifyData.matched ? (
                    <>
                      <Warning sx={{ fontSize: 18 }} />
                      <span>Settlement Blocked — Mismatch</span>
                    </>
                  ) : (
                    <>
                      <Check sx={{ fontSize: 18 }} />
                      <span>Confirm & Settle</span>
                    </>
                  )}
                </SubmitBtn>
              </DialogBody>
            </form>
          </DialogContainer>
        )}
      </Dialog>
    </>
  );
}

export default SettlementDialog;
